import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import { useMultiAccount, Account } from '../../hooks/useMultiAccount';
import { PrayerRequestButton } from '../Prayer';
import OnboardingResumeBanner from './OnboardingResumeBanner';
import './BridgeClubLayout.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface BridgeClubLayoutProps {
  children: React.ReactNode;
  hideNav?: boolean;
}

const BridgeClubLayout: React.FC<BridgeClubLayoutProps> = ({ children, hideNav = false }) => {
  const { member, logout } = useBridgeClubAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Multi-account support
  const { accounts, hasMultipleAccounts } = useMultiAccount();
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/bridge-club/login');
  };

  const handleSwitchAccount = async (account: Account) => {
    setSwitchingAccount(true);
    setSwitchError(null);

    try {
      const body: any = {
        account_type: account.type,
      };

      if (account.portal_user_id) {
        body.portal_user_id = account.portal_user_id;
      }
      if (account.member_id) {
        body.member_id = account.member_id;
      }

      const response = await fetch(`${API_BASE_URL}/auth/universal-login/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.user_type === 'portal') {
          localStorage.setItem('portal_user', JSON.stringify(data.user));
          localStorage.removeItem('bridge_club_member');
        } else if (data.user_type === 'bridge_club') {
          localStorage.setItem('bridge_club_member', JSON.stringify(data.member));
          localStorage.removeItem('portal_user');
        }

        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (account.type === 'bridge_club') {
          window.location.href = '/bridge-club/dashboard';
        } else {
          window.location.href = '/portal/dashboard';
        }
      } else {
        setSwitchError(data.error || 'Failed to switch account');
      }
    } catch (err: any) {
      setSwitchError('Network error. Please try again.');
    } finally {
      setSwitchingAccount(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="bridge-club-layout">
      {/* Header */}
      <header className="bridge-club-header">
        <Link to="/bridge-club/dashboard" className="header-logo">
          <img src="/rbf-logo.png" alt="Red Bridge Farm" />
        </Link>

        <div className="header-center">
          <h1 className="header-title">Bridge Club</h1>
          <p className="header-subtitle">By Red Bridge Farm Feed Co.</p>
          <p className="header-tagline">Healthy Feed. Healthy Food. Healthy Families.</p>
        </div>

        {member ? (
          <div className="header-actions">
            {hasMultipleAccounts && (
              <button
                onClick={() => setShowAccountSwitcher(true)}
                className="header-switch"
                title="Switch to another account"
              >
                ⇄
              </button>
            )}
            <button onClick={handleLogout} className="header-logout">
              Logout
            </button>
          </div>
        ) : (
          <div className="header-placeholder" />
        )}
      </header>

      {/* Account Switcher Modal */}
      {showAccountSwitcher && (
        <div className="bc-account-switcher-overlay" onClick={() => setShowAccountSwitcher(false)}>
          <div className="bc-account-switcher-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="bc-account-switcher-close"
              onClick={() => setShowAccountSwitcher(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <h2 className="bc-account-switcher-title">Switch Account</h2>
            <p className="bc-account-switcher-subtitle">
              Select an account to switch to:
            </p>

            <div className="bc-account-switcher-list">
              {accounts.map((account, index) => (
                <button
                  key={index}
                  className={`bc-account-switcher-option ${account.is_current ? 'current' : ''}`}
                  onClick={() => !account.is_current && handleSwitchAccount(account)}
                  disabled={switchingAccount || account.is_current}
                >
                  <span className="bc-account-switcher-icon">
                    {account.icon || (account.type === 'bridge_club' ? '🌾' : '🏪')}
                  </span>
                  <div className="bc-account-switcher-info">
                    <div className="bc-account-switcher-label">
                      {account.label}
                      {account.is_current && <span className="bc-current-badge">Current</span>}
                    </div>
                    <div className="bc-account-switcher-desc">{account.subtitle}</div>
                  </div>
                  {!account.is_current && <span className="bc-account-switcher-arrow">→</span>}
                </button>
              ))}
            </div>

            {switchingAccount && (
              <p className="bc-account-switcher-loading">Switching account...</p>
            )}

            {switchError && (
              <div className="bc-account-switcher-error">{switchError}</div>
            )}
          </div>
        </div>
      )}

      {/* The way back into an unfinished delivery setup. Renders nothing for
          members who are not delivery subscribers (the common case) or whose
          onboarding is complete. Sits ABOVE the main content and inside the
          layout, so it is present on every page that uses the layout rather
          than being remembered per-page. */}
      {member && <OnboardingResumeBanner />}

      {/* Main Content */}
      <main className="bridge-club-main">
        {children}

        {/* Prayer request link - pinned to the bottom of the screen area by the layout's
            column (BridgeClubLayout.css), so it is never the first thing on a page that
            is still loading. Quiet by design (Brad, 2026-09-25). */}
        {member && (
          <PrayerRequestButton
            portalSource="bridge_club"
            userName={`${member.first_name} ${member.last_name}`}
            userEmail={member.email}
            submitEndpoint="/bridge-club/prayer-request/"
          />
        )}
      </main>

      {/* Bottom Navigation */}
      {!hideNav && member && (
        <nav className="bridge-club-nav">
          <Link
            to="/bridge-club/dashboard"
            className={`nav-item ${isActive('/bridge-club/dashboard') ? 'active' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <span>Home</span>
          </Link>

          <Link
            to="/bridge-club/card"
            className={`nav-item ${isActive('/bridge-club/card') ? 'active' : ''}`}
          >
            <img src="/rbf-favicon.png" alt="Club Card" className="nav-icon-img" style={{ width: 28, height: 28, objectFit: 'contain' }} />
            <span>Card</span>
          </Link>

          <Link
            to="/bridge-club/animals"
            className={`nav-item ${isActive('/bridge-club/animals') ? 'active' : ''}`}
          >
            <span className="nav-emoji">🐔</span>
            <span>Animals</span>
          </Link>

          <Link
            to="/bridge-club/feed"
            className={`nav-item ${isActive('/bridge-club/feed') ? 'active' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span>Feed</span>
          </Link>

          <Link
            to="/bridge-club/profile"
            className={`nav-item ${isActive('/bridge-club/profile') ? 'active' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            <span>Profile</span>
          </Link>
        </nav>
      )}
    </div>
  );
};

export default BridgeClubLayout;
